/**
 * HolisticCalendar — Phase 11 Task 11.1-G (Screen 4)
 * Admin-only. Web only — desktop layout.
 * Week-grid view: rows = healers, columns = Mon–Sun for the current week.
 *
 * GET /api/admin/healer-slots          — today's session counts, availability, tier
 * GET /api/admin/healer-schedule/centre — all healers' working days and blocked slots
 *
 * Today cell: live session count (N/Cap) coloured by load.
 * Other days: working pattern (● / Blocked / Off) from centre schedules.
 * Session counts are only available for today — all other day cells are pattern-only.
 * Prev/Next week navigation: pattern cells still work, today badge only on current week.
 */

import React, { useState, useEffect, useCallback } from 'react';
import api from '../../utils/api';

const TEAL   = '#4CE3CD';
const AMBER  = '#F39C12';
const GREEN  = '#27AE60';
const RED    = '#E74C3C';
const GREY   = '#95A5A6';
const PURPLE = '#8E44AD';

const TIER_COLORS = { standard: TEAL, premium: AMBER, exclusive: PURPLE };
const DAY_KEYS    = ['monday','tuesday','wednesday','thursday','friday','saturday','sunday'];
const DAY_ABBREV  = ['Mon','Tue','Wed','Thu','Fri','Sat','Sun'];

function getToday() {
  const t = new Date(); t.setHours(0, 0, 0, 0); return t;
}

function isSameDay(a, b) {
  return a.getFullYear() === b.getFullYear() &&
         a.getMonth()    === b.getMonth()    &&
         a.getDate()     === b.getDate();
}

function buildWeek(weekOffset) {
  const today  = getToday();
  const dow    = today.getDay();
  const diff   = dow === 0 ? -6 : 1 - dow;
  const monday = new Date(today);
  monday.setDate(today.getDate() + diff + weekOffset * 7);
  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(monday);
    d.setDate(monday.getDate() + i);
    return d;
  });
}

function isBlockedOnDay(blockedSlots = [], day) {
  const dayStart = new Date(day); dayStart.setHours(0, 0, 0, 0);
  const dayEnd   = new Date(day); dayEnd.setHours(23, 59, 59, 999);
  return blockedSlots.some(b => {
    if (b.allDay) {
      const s = new Date(b.startDate); s.setUTCHours(0, 0, 0, 0);
      const e = new Date(b.endDate);   e.setUTCHours(23, 59, 59, 999);
      return dayStart <= e && dayEnd >= s;
    }
    return b.date && isSameDay(new Date(b.date), day);
  });
}

function formatWeekRange(week) {
  const opts = { day: 'numeric', month: 'short' };
  return `${week[0].toLocaleDateString('en-GB', opts)} – ${week[6].toLocaleDateString('en-GB', { ...opts, year: 'numeric' })}`;
}

function badge(bg, color, text) {
  return (
    <span style={{
      display: 'inline-block', padding: '2px 8px', borderRadius: 12,
      backgroundColor: bg, color, fontSize: 11, fontWeight: 700,
    }}>
      {text}
    </span>
  );
}

function CellContent({ isToday, isCurrentWeek, slotData, schedule, day }) {
  const dow     = day.getDay();
  const dayKey  = DAY_KEYS[dow === 0 ? 6 : dow - 1];
  const isOff   = schedule ? !(schedule.workingDays || []).includes(dayKey) : false;
  const blocked = schedule ? isBlockedOnDay(schedule.blockedSlots, day) : false;

  // Today (current week only): use live healer-slots data
  if (isToday && isCurrentWeek && slotData) {
    if (slotData.blockedToday)   return badge(RED,  '#fff', 'Blocked');
    if (!slotData.availableToday) return badge(GREY, '#fff', 'Paused');
    const full  = slotData.sessionsToday >= slotData.dailyCapacity;
    const label = `${slotData.sessionsToday}/${slotData.dailyCapacity}`;
    return badge(full ? AMBER : TEAL, full ? '#fff' : '#0D0F1F', full ? `Full ${label}` : label);
  }

  // Non-today: compute from schedule
  if (blocked) return badge(RED, '#fff', 'Blocked');
  if (isOff)   return <span style={{ color: GREY,  fontSize: 13 }}>Off</span>;
  return         <span style={{ color: GREEN, fontSize: 18 }}>●</span>;
}

function LegendDot({ color, label }) {
  return (
    <span style={{ display:'flex', alignItems:'center', gap:5, fontSize:12, color:'#555' }}>
      <span style={{ color, fontSize:16 }}>●</span>{label}
    </span>
  );
}
function LegendBadge({ bg, color = '#fff', label }) {
  return badge(bg, color, label);
}

export default function HolisticCalendar() {
  const [weekOffset, setWeekOffset] = useState(0);
  const [slots,      setSlots]      = useState([]);
  const [schedules,  setSchedules]  = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [error,      setError]      = useState('');

  const week          = buildWeek(weekOffset);
  const today         = getToday();
  const isCurrentWeek = weekOffset === 0;

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError('');
    const [slotsResult, schedResult] = await Promise.allSettled([
      api.get('/admin/healer-slots'),
      api.get('/admin/healer-schedule/centre'),
    ]);
    if (slotsResult.status === 'fulfilled') setSlots(slotsResult.value.data.data || []);
    if (schedResult.status === 'fulfilled') setSchedules(schedResult.value.data.data || []);
    if (slotsResult.status === 'rejected' && schedResult.status === 'rejected') {
      setError('Could not load calendar data. Please refresh.');
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Index slot data by healerId string for O(1) lookup per row
  const slotMap = {};
  slots.forEach(s => { slotMap[String(s.healerId)] = s; });

  // Build healer rows from centre schedules (populated healerId)
  const healerRows = schedules
    .filter(s => s.healerId && s.healerId.isActive !== false && s.healerId.role === 'healer')
    .map(s => {
      const id      = String(s.healerId._id || s.healerId);
      const slotData = slotMap[id];
      return {
        healerId:  id,
        name:      `${s.healerId.firstName || ''} ${s.healerId.lastName || ''}`.trim() || '—',
        schedule:  s,
        slotData,
        tier:      slotData?.healerTier || null,
      };
    });

  return (
    <div style={s.container}>

      {/* ── Header ─────────────────────────────────────────────────────── */}
      <div style={s.header}>
        <h2 style={s.title}>Holistic Calendar</h2>
        <div style={s.navRow}>
          <button style={s.navBtn} onClick={() => setWeekOffset(w => w - 1)}>← Prev</button>
          <span style={s.weekLabel}>{formatWeekRange(week)}</span>
          <button style={s.navBtn} onClick={() => setWeekOffset(w => w + 1)}>Next →</button>
          {weekOffset !== 0 && (
            <button style={s.todayBtn} onClick={() => setWeekOffset(0)}>Today</button>
          )}
          <button style={s.refreshBtn} onClick={fetchData} disabled={loading}>↺</button>
        </div>
      </div>

      {/* ── Legend ─────────────────────────────────────────────────────── */}
      <div style={s.legend}>
        <LegendDot  color={GREEN} label="Working" />
        <LegendBadge bg={TEAL}  color="#0D0F1F" label="N/Cap (today)" />
        <LegendBadge bg={AMBER} label="Full (today)" />
        <LegendBadge bg={RED}   label="Blocked" />
        <LegendDot  color={GREY} label="Off / Paused" />
      </div>

      {error && <p style={{ color: RED, marginBottom: 12, fontSize: 13 }}>{error}</p>}

      {/* ── Calendar grid ──────────────────────────────────────────────── */}
      {loading ? (
        <p style={{ color: '#999', padding: '24px 0' }}>Loading…</p>
      ) : (
        <div style={s.tableWrap}>
          <table style={s.table}>
            <thead>
              <tr>
                <th style={{ ...s.th, ...s.healerCol }}>Healer</th>
                {week.map((day, i) => {
                  const isToday = isSameDay(day, today);
                  return (
                    <th key={i} style={{ ...s.th, ...(isToday ? s.todayTh : {}) }}>
                      <div style={s.dayAbbrev}>{DAY_ABBREV[i]}</div>
                      <div style={isToday ? s.todayNum : s.dayNum}>{day.getDate()}</div>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {healerRows.length === 0 ? (
                <tr>
                  <td colSpan={8} style={{ textAlign:'center', padding:32, color:GREY, fontSize:14 }}>
                    No healer schedules found. Schedules are created on first login.
                  </td>
                </tr>
              ) : healerRows.map(({ healerId, name, schedule, slotData, tier }) => (
                <tr key={healerId} style={s.row}>
                  <td style={s.healerCell}>
                    <div style={s.healerName}>{name}</div>
                    {tier && (
                      <span style={{ fontSize:10, fontWeight:700, color: TIER_COLORS[tier] || GREY, textTransform:'uppercase', letterSpacing:0.5 }}>
                        {tier}
                      </span>
                    )}
                  </td>
                  {week.map((day, i) => {
                    const isToday = isSameDay(day, today);
                    return (
                      <td key={i} style={{ ...s.td, ...(isToday ? s.todayTd : {}) }}>
                        <CellContent
                          isToday={isToday}
                          isCurrentWeek={isCurrentWeek}
                          slotData={slotData}
                          schedule={schedule}
                          day={day}
                        />
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <p style={s.footnote}>
        Session counts (N/Cap) are live for today only. Other days show working pattern from schedule.
      </p>
    </div>
  );
}

const s = {
  container:  { fontFamily: 'system-ui, sans-serif', padding: '16px 0' },
  header:     { display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:12, flexWrap:'wrap', gap:8 },
  title:      { margin:0, fontSize:18, fontWeight:700, color:'#1a1a2e' },
  navRow:     { display:'flex', alignItems:'center', gap:10 },
  navBtn:     { padding:'6px 14px', borderRadius:8, border:'1px solid #ddd', background:'#fff', cursor:'pointer', fontSize:13, fontWeight:600, color:'#333' },
  weekLabel:  { fontSize:14, fontWeight:600, color:'#444', minWidth:180, textAlign:'center' },
  todayBtn:   { padding:'6px 12px', borderRadius:8, border:'none', background:TEAL, color:'#0D0F1F', fontWeight:700, cursor:'pointer', fontSize:12 },
  refreshBtn: { padding:'6px 10px', borderRadius:8, border:'1px solid #eee', background:'#fafafa', cursor:'pointer', fontSize:15, color:'#888' },
  legend:     { display:'flex', gap:20, marginBottom:16, flexWrap:'wrap', alignItems:'center' },
  tableWrap:  { overflowX:'auto', border:'1px solid #eee', borderRadius:10 },
  table:      { width:'100%', borderCollapse:'collapse', minWidth:680 },
  th:         { padding:'10px 12px', textAlign:'center', fontSize:12, fontWeight:600, color:'#666', borderBottom:'1px solid #eee', background:'#fafafa' },
  healerCol:  { textAlign:'left', minWidth:150, paddingLeft:16 },
  todayTh:    { background:'#f0fdf9', color:'#0a8f7a' },
  dayAbbrev:  { fontSize:10, color:'#999', textTransform:'uppercase', letterSpacing:0.5 },
  dayNum:     { fontSize:16, fontWeight:700, color:'#333', marginTop:2 },
  todayNum:   { fontSize:16, fontWeight:800, color:TEAL, marginTop:2 },
  row:        { borderBottom:'1px solid #f5f5f5' },
  td:         { padding:'12px 8px', textAlign:'center', fontSize:13 },
  todayTd:    { background:'#f8fffe' },
  healerCell: { padding:'12px 16px', textAlign:'left' },
  healerName: { fontWeight:600, color:'#1a1a2e', fontSize:14, marginBottom:2 },
  footnote:   { fontSize:11, color:'#aaa', marginTop:12 },
};
